import { Link, useSearchParams } from "react-router-dom";
import { PAYOUT_STATUS_LABELS, type PayoutStatus } from "@ticketnjoy/types";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { QueryError } from "@/components/query-error";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PAYOUT_PAGE_SIZE, usePayoutsQuery, type PayoutListFilter } from "@/features/api";
import { dateOnly, money, payoutStatusVariant } from "@/lib/format";

const STATUS_FILTERS: PayoutStatus[] = ["Requested", "Approved", "Paid", "Rejected", "Cancelled"];
const OPEN = "open";
const ALL = "all";

/** Filtre URL'de: yoksa "open" (aksiyon bekleyenler), "all" = hepsi. */
export function PayoutsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get("status") ?? OPEN;
  const status: PayoutListFilter = raw === ALL ? "" : (raw as PayoutListFilter);
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);

  function update(next: { status?: string; page?: number }) {
    const params = new URLSearchParams(searchParams);
    const nextStatus = next.status ?? raw;
    const nextPage = next.page ?? 1;
    if (nextStatus === OPEN) params.delete("status");
    else params.set("status", nextStatus);
    if (nextPage === 1) params.delete("page");
    else params.set("page", String(nextPage));
    setSearchParams(params, { replace: true });
  }

  const { data, isLoading, isFetching, error, refetch } = usePayoutsQuery({ status, page });
  const pageCount = data ? Math.max(1, Math.ceil(data.total / PAYOUT_PAGE_SIZE)) : 1;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Ödemeler</h1>
          <p className="text-sm text-muted-foreground">
            Organizatörlere yapılan ödemeler. İşlemler organizatör detayındaki Cari kartından yapılır.
          </p>
        </div>
        <Select value={raw} onValueChange={(v) => update({ status: v })}>
          <SelectTrigger className="sm:w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={OPEN}>Aksiyon bekleyenler</SelectItem>
            {STATUS_FILTERS.map((s) => (
              <SelectItem key={s} value={s}>
                {PAYOUT_STATUS_LABELS[s]}
              </SelectItem>
            ))}
            <SelectItem value={ALL}>Tümü</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="py-0">
        <CardContent className="px-0">
          {error ? (
            <QueryError
              className="m-6"
              message="Liste yüklenemedi."
              onRetry={() => refetch()}
              retrying={isFetching}
            />
          ) : isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Yükleniyor...</p>
          ) : data && data.items.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">
              {raw === OPEN ? "Bekleyen ödeme yok." : "Kayıt bulunamadı."}
            </p>
          ) : (
            <div className={isFetching ? "overflow-x-auto opacity-60" : "overflow-x-auto"}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Organizatör</th>
                    <th className="px-4 py-3 text-right font-medium">Tutar</th>
                    <th className="px-4 py-3 font-medium">Durum</th>
                    <th className="px-4 py-3 font-medium">Kaynak</th>
                    <th className="px-4 py-3 font-medium">Oluşturma</th>
                    <th className="px-4 py-3 font-medium">Ödeme</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((p) => (
                    <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="px-4 py-3">
                        <Link to={`/organizers/${p.organizerId}`} className="font-medium hover:underline">
                          {p.companyName}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{money(p.amount, p.currency)}</td>
                      <td className="px-4 py-3">
                        <Badge variant={payoutStatusVariant(p.status)}>{PAYOUT_STATUS_LABELS[p.status]}</Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {p.source === "Admin" ? "Admin" : "Talep"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{dateOnly(p.createdAt)}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{dateOnly(p.paidAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => update({ page: page - 1 })}>
            Önceki
          </Button>
          <span className="text-muted-foreground">
            {page} / {pageCount}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pageCount}
            onClick={() => update({ page: page + 1 })}
          >
            Sonraki
          </Button>
        </div>
      )}
    </div>
  );
}
